<template>
    <!-- Display panels for selecting content -->
    <div v-if="!getTransition">
        <v-card outlined class="header text-center">
            <v-card-title class="text-wrap">Build Therapy Plan</v-card-title>
            <v-card-text>
                Select exercises, videos, and readings for your patient's therapy plan.
            </v-card-text>
        </v-card>
        <v-row justify="center">
            <v-col cols="12" class="d-flex justify-center">
                <v-btn class="bg-aqua-teal" @click="filterDialog = true">Select Filters</v-btn>
                <!-- Dialog for Filter options -->
                <v-dialog :model-value="filterDialog" persistent :max-width="500">
                    <v-card>
                        <v-card-title class="bg-aqua-teal text-center text-wrap">Filter Options</v-card-title>
                        <v-card-text>
                            <v-row justify="center">
                                <v-col cols="12">
                                    <!-- General Search -->
                                    <v-text-field v-model="searchQuery" label="Search (any keyword)" outlined
                                        dense></v-text-field>
                                </v-col>
                                <v-col cols="12">
                                    <v-select v-model="selectedCategories" :items="categoryOptions"
                                        label="Multi-Select Categories" outlined dense multiple></v-select>
                                </v-col>
                                <v-col cols="12">
                                    <v-select v-model="selectedPages" :items="pageOptions"
                                        label="Select Page" outlined dense></v-select>
                                </v-col>

                            </v-row>
                        </v-card-text>
                        <v-divider></v-divider>
                        <v-card-actions>
                            <v-row no-gutters>
                                <v-col cols="6" class="pa-2">
                                    <v-btn class="bg-aqua-teal" block @click="clearFilters()">
                                        Clear
                                    </v-btn>
                                </v-col>
                                <v-col cols="6" class="pa-2">
                                    <v-btn class="review-button" block @click="applyFilters()">
                                        Apply
                                    </v-btn>
                                </v-col>
                                <v-col cols="12" class="pa-2">
                                    <v-btn block class="bg-aqua-teal mt-2" @click="closeFilterDialog()">
                                        Close
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-card-actions>
                    </v-card>

                </v-dialog>
            </v-col>
            <v-col></v-col>
            <v-divider />
            <v-col cols="12">
                <SelectionToggle v-model:choice="choice" v-model:name="text" />
            </v-col>

            <v-divider class="border-opacity-25"></v-divider>

            <v-virtual-scroll :items="['1']" class="mt-2" :max-height="scrollHeight">
                <v-row class="pa-8" justify="center">
                    <v-col cols="12">
                        <ContentPanel :contents="contents" :contentGroup="categories[text]"
                            @add-selection="addSelection" @remove-selection="removeSelection"
                            :selections="selections" :filter="getFilters"/>
                    </v-col>
                </v-row>
            </v-virtual-scroll>
        </v-row>
    </div>

    <!-- Display selected content for review -->
    <div v-if="getTransition === 1">
        <v-card outlined class="header text-center">
            <v-card-title class="text-wrap">Review Therapy Plan</v-card-title>
            <v-card-text>
                Review selected exercises, videos, and readings for your patient's therapy plan.
            </v-card-text>
        </v-card>
        <v-virtual-scroll :items="['1']" class="mt-2" :max-height="scrollHeight">
            <v-row class="pa-8" justify="center">
                <v-col cols="12" md="6" no-gutters v-for="(content, index) in Object.keys(selections)" :key="index">
                    <ItemCard :item="selections[content]" @add-selection="addSelection"
                        @remove-selection="removeSelection"
                        :in-cart="`${selections[content].contentId}` in getSelections" :can-add-task-notes="true" />
                </v-col>
            </v-row>
        </v-virtual-scroll>
    </div>

    <div v-if="getTransition === 2">
        <v-form v-model="isValid">
            <v-card>
                <v-card-title class="bg-aqua-teal text-wrap text-center mb-2"> Create Your Patient's Therapy Plan
                </v-card-title>
                <v-card-text>
                    <v-text-field label="Plan Name" v-model="plan.name" clearable outlined :rules="[rules.text]" />

                    <v-textarea label="Plan Description" v-model="plan.description" clearable outlined
                        :hint="'Enter an optional plan description.'" />
                </v-card-text>
                    <div class="d-flex justify-center">
                        <v-select v-model="plan.status" label="Plan Status" :items="['Active', 'Inactive']"
                    outlined dense :max-width="300" hint="Make plan active or inactive" persistent-hint/>
                    </div>
            </v-card>

        </v-form>
    </div>
    <div v-if="getTransition === 3">
        <v-card outlined class="header text-center">
            <v-card-title class="text-wrap">Therapy Plan {{ getCompletionMessage }}</v-card-title>
        </v-card>
        <v-virtual-scroll :items="['1']" class="mt-2" :max-height="scrollHeight">
            <v-row class="pa-8" justify="center">
                <v-col cols="12" md="6" no-gutters v-for="(content, index) in Object.keys(selections)" :key="index">
                    <ItemCard :item="selections[content]" :no-actions="true" />
                </v-col>
            </v-row>
        </v-virtual-scroll>
    </div>

    <v-row justify="center">
        <v-col cols="12" class="pa-8">
            <div class="d-flex justify-center mt-2">
                <v-btn v-if="getTransition !== 3" class="bg-aqua-teal mr-2" @click="prev()">
                    Back
                </v-btn>
                <!-- Review Button -->
                <v-btn v-if="getTransition === 0" class="review-button" :disabled="getItemCount <= 0" @click="next()">
                    Review Plan
                </v-btn>
                <v-btn v-if="getTransition === 1" class="review-button" @click="next()">
                    Finalize Plan
                </v-btn>
                <v-btn v-if="getTransition === 2" class="review-button" :disabled="!isValid" @click="savePlan()">
                    Save Plan
                </v-btn>
                <v-btn v-if="getTransition === 3" class="review-button" @click="goBack()">
                    Done
                </v-btn>
            </div>
        </v-col>
    </v-row>
    <cart-badge :count="getItemCount" :display="false" :display-snackbar="displaySnackbar" />
</template>

<script>
import axios from 'axios';
import CartBadge from './CartBadge.vue';
import ContentPanel from './ContentPanel.vue';
import ItemCard from './ItemCard.vue';
// import SearchBar from './SearchBar.vue';
import SelectionToggle from './SelectionToggle.vue';

export default {
    name: "ContentSelectionPage",
    components: {
        CartBadge,
        ContentPanel,
        ItemCard,
        // SearchBar,
        SelectionToggle
    },
    data() {
        return {
            isValid: false,
            transition: 0,
            text: "Educational",
            choice: 0,
            updatedPlanStatus: "",
            filterDialog: false,
            filters: {
                'searchQuery': "",
                'selectedCategories': [],
                'selectedPages': null,
                'showFavorites': false
            },
            queryItems: {},
            contents: [],
            pageOptions: [],
            patients: [],
            plan: {
                'name': "",
                'description': "",
                'status': "Active"
            },
            providerId: null,
            patientId: null, 
            existingPlan: {},
            existingSelections: {},
            selections: {},
            searchQuery: "",
            showFavorites: false,
            selectedCategories: [],
            selectedPages: null,
            displaySnackbar: false,
            categoryOptions: ["Breathwork", "Gratitude", "Meditation", "Self Talk"],
            categories: {
                "Educational": ["Learning Library"],
                "Coping": ["Coping Techniques"],
                "Processing": [],
                "Assessments": []
            },
            rules: {
                text: v => {
                    if (typeof (v) === "string") {
                        // If string is non-empty, non-white
                        if (v.trim().length > 0) {
                            return true;
                        }
                    }
                    return "Please enter a Plan Name";
                }
            }
        }
    },
    /* Props for creating plan and editing current plans. */
    async mounted() {
        /* Await in async function asures that these functions will run in order. */
        await this.setProps();
        await this.fetchData();
        await this.fetchPlanToEdit();
        if (this.queryItems.isEdit) {
            await this.fetchSelectedContent();
        } else {
            console.log("Undefined");
        }
    },
    methods: {
        async fetchData() {
            try {
                axios.defaults.baseURL = process.env.VUE_APP_API_URL;
                this.contents = [];
                let contentGroup = this.categories[this.text];
                for (let i = 0; i < contentGroup.length; i++) {
                    var response = await axios.get(`/api/unifiedcontent/categories/${contentGroup[i]}`);
                    var data = response.data;
                    data.forEach(data => this.contents.push(data));
                }

                // Populate page and category options arrays for filter selection
                this.pageOptions = [];
                this.categoryOptions = [];
                for (let i = 0; i < contentGroup.length; i++){
                    /* Fetch info based on current content group */
                    // Get page titles
                    let response = await axios.get(`/api/unifiedcontent/contentGroup/${contentGroup[i]}/pageTitles`);
                    let responseData = response.data;
                    responseData.forEach(data => this.pageOptions.push(data));
                   
                    // Get subpage titles
                    response = await axios.get(`/api/unifiedcontent/contentGroup/${contentGroup[i]}/subPageTitles`);
                    responseData = response.data; 
                    responseData.forEach(data => this.pageOptions.push(data))
                    
                    // Get categories 
                    response = await axios.get(`/api/unifiedcontent/categories/${contentGroup[i]}`);
                    responseData = response.data;
                    responseData.forEach(data => this.categoryOptions.push(data));
                }
            } catch (error) {
                console.log(error);
            }
        },
        async setProps() {
            try {
                axios.defaults.baseURL = process.env.VUE_APP_API_URL;

                // Decrypt encrypted query props through backend
                const request = {
                    encryptedData: [
                        this.$route.query.planId
                    ]
                };

                const response = await axios.post(`/api/encryption/decrypt/array`, request);

                // Set decrypted query props into object
                const values = response.data;
                this.providerId = localStorage.getItem("providerId");
                this.patientId = this.$route.query.patientId;
                // Set plan id to null if no current plan 
                this.queryItems['planId'] = (values[2] === "null") ? null : values[0];

                // Set remaining query props into object
                this.queryItems['isEdit'] = (this.$route.query.isEdit === "true") ? true : false;
                this.queryItems['isNew'] = (this.$route.query.isNew === "true") ? true : false;

            } catch (error) {
                console.log(error)
            }

        },
        async fetchSelectedContent() {
            if (this.queryItems.planId != null) {
                try {
                    axios.defaults.baseURL = process.env.VUE_APP_API_URL;

                    // Get assigned tasks under plan
                    let response = await axios.get(`/api/assignedtask/${this.queryItems.planId}`);
                    const tasks = response.data;
                    var ids = [];

                    // Get tasks content ids
                    for (let i = 0; i < tasks.length; i++) {
                        this.existingSelections[tasks[i]['contentId']];
                        ids.push(tasks[i]['contentId']);
                    }

                    response = await axios.post('/api/unifiedcontent/contentid/array', {
                        ids: ids,
                    });

                    const contents = response.data;

                    // Concatenate assigned task to corresponding content 
                    for (let i = 0; i < ids.length; i++) {
                        this.existingSelections[contents[i]['contentId']] = Object.assign(contents[i], tasks[i]);
                        this.selections[contents[i]['contentId']] = this.existingSelections[contents[i]['contentId']];
                    }

                    this.selections = this.alphabetical_sort_object_of_objects(this.selections, "contentBlock");
                } catch (error) {
                    console.log(error);
                }
            } else {
                console.log("Nothing");
            }
        },
        async fetchPlanToEdit() {
            if (this.queryItems.isEdit) {
                try {
                    axios.defaults.baseURL = process.env.VUE_APP_API_URL;

                    var callUrl = (this.queryItems.planId != null)
                        ? `/api/therapyplan/${this.queryItems.planId}` :
                        `/api/therapyplan/patient/${this.patientId}`;
                    const response = await axios.get(callUrl);
                    const plan = response.data;
                    this.existingPlan = plan;
                    this.plan.name = this.existingPlan.planName;
                    this.plan.description = this.existingPlan.planDescription;
                    this.plan.status = this.existingPlan.planStatus;
                } catch (error) {
                    console.log(error);
                }
            }
        },
        addSelection(item) {
            if (this.displaySnackbar === false) this.displaySnackbar = true;
            this.selections[item.contentId] = item;
            this.selections = this.alphabetical_sort_object_of_objects(this.selections, "contentBlock");
        },
        removeSelection(item) {
            if (this.displaySnackbar === false) this.displaySnackbar = true;
            delete this.selections[item.contentId];
        },
        goBack() {
            this.$router.back();
        },
        prev() {
            if (this.transition > 0) {
                this.transition--;
            } else {
                this.goBack();
            }
        },
        next() {
            this.transition++;
        },
        async savePlan() {
            axios.defaults.baseURL = process.env.VUE_APP_API_URL;
            if (this.queryItems.isEdit && Object.keys(this.existingPlan).length > 0) {  // Update existing plan
                try {
                    this.existingPlan.planName = this.plan.name;
                    this.existingPlan.planDescription = this.plan.description;
                    this.existingPlan.planStatus = this.plan.status;
                    await axios.put(`/api/therapyplan/${this.queryItems.planId}?status=${this.existingPlan.planStatus}`, this.existingPlan);
                    await this.assignTasks(this.queryItems.planId);
                } catch (error) {
                    console.log(error);
                }
            }

            if (this.queryItems.isNew) {  // Add new plan to database
                try {
                    const newPlan = {
                        encryptedProviderId: this.providerId,
                        encryptedPatientId: this.patientId,
                        patientId: "00000000-0000-0000-0000-000000000000",
                        providerId: "00000000-0000-0000-0000-000000000000",
                        planName: this.plan.name,
                        planDescription: this.plan.description,
                        planStatus: this.plan.status,
                    }

                    const response = await axios.post(`/api/therapyplan?status=${this.plan.status}`, newPlan);
                    const temporary = response.data;
                    await this.assignTasks(temporary.planId);
                    this.queryItems.planId = temporary.planId;
                } catch (error) {
                    console.log(error);
                }
            }

            this.next();
        },
        async assignTasks(targetPlanId) {
            try {
                axios.defaults.baseURL = process.env.VUE_APP_API_URL;
                // Get the patient's plan
                const response = await axios.get(`/api/therapyplan/${targetPlanId}`);
                const targetPlan = response.data;
                const selectionKeys = Object.keys(this.selections);
                var hasKey = (v, k) => Object.hasOwn(v, k);

                var existingTasksObject = {};  // For updating existing tasks
                var existingTasksArray = [];
                var newTasksObject = {};  // For posting new tasks
                var newTasksArray = [];
                var deselectedTasks = [];  // For deleting existing tasks

                for (let i = 0; i < selectionKeys.length; i++) {
                    // Add the remaining key-value pairs for assigned task object. 
                    let ob = this.selections[selectionKeys[i]];
                    if (!hasKey(ob, 'planId')) ob['planId'] = targetPlan.planId;
                    if (!hasKey(ob, 'status')) ob['status'] = "Not started";
                    if (!hasKey(ob, 'taskCompletionDate')) ob['taskCompletionDate'] = null;
                    if (!hasKey(ob, 'isModified')) ob['isModified'] = null;
                    if (!hasKey(ob, 'updatedBy')) ob['updatedBy'] = "Not started";
                    ob['sortOrder'] = i + 1;

                    if (hasKey(ob, 'assignedTaskId')) {  // Get tasks to update
                        existingTasksObject[ob.contentId] = ob;
                        existingTasksArray.push(ob);
                    } else {
                        newTasksObject[ob.contentId] = ob;
                        newTasksArray.push(ob);  // Get tasks to add
                    }
                }

                // Get ids of tasks to delete 
                var existingSelectionKeys = Object.keys(this.existingSelections);
                for (let j = 0; j < existingSelectionKeys.length; j++) {
                    var ob = this.existingSelections[existingSelectionKeys[j]];
                    if (!(ob.contentId in existingTasksObject)) {
                        deselectedTasks.push(ob.assignedTaskId);
                    }
                }

                // Add new tasks
                if (newTasksArray.length > 0) {
                    await axios.post(`api/assignedtask/multiassignment/${targetPlan.planId}`, {
                        Tasks: newTasksArray,
                    });
                }

                // Remove deselected tasks
                if (deselectedTasks.length > 0) {
                    await axios.delete(`/api/assignedtask/delete`, {
                        data: {
                            Ids: deselectedTasks,
                        }
                    });

                    // Remove key-value pair for deleted objects
                    deselectedTasks.forEach((key) => delete this.existingSelections[key]);
                }

                // Update assigned task
                var numToUpdate = existingTasksArray.length;
                if (numToUpdate > 0) {
                    for (let t = 0; t < numToUpdate; t++) {
                        var task = existingTasksArray[t];
                        await axios.put(`/api/assignedtask/${task.assignedTaskId}`, task)
                    }
                }
            } catch (error) {
                console.log(error);
            }
        },
        applyFilters() {
            this.filters = {
                'searchQuery': this.searchQuery,
                'selectedCategories': this.selectedCategories,
                'selectedPages': this.selectedPages,
                'showFavorites': this.showFavorites
            };
        },
        clearFilters() {
            this.searchQuery = "";
            this.showFavorites = false;
            this.selectedCategories = [];
            this.selectedPages = null;

            this.applyFilters();
        },
        closeFilterDialog(){
            this.searchQuery = this.filters['searchQuery'];
            this.selectedCategories = this.filters['selectedCategories'];
            this.showFavorites = this.filters['showFavorites'];
            this.selectedPages = this.filters['selectedPages'];

            this.filterDialog = false; 
        },
        alphabetical_sort_object_of_objects(data, attr) {
            var arr = [];

            // Extract identification and sort keys
            for (let prop in data) {
                if (Object.hasOwn(data, prop)) {
                    let obj = {};
                    obj[prop] = data[prop];
                    obj.tempSortName = data[prop][attr].toLowerCase();
                    arr.push(obj);
                }
            }

            // Arrange each object by the sort key
            arr.sort((a, b) => {
                var at = a.tempSortName,
                    bt = b.tempSortName;
                return at > bt ? 1 : (at < bt ? -1 : 0);
            });

            // Arrange each actual object by the sorted order
            var result = [];
            for (var i = 0, l = arr.length; i < l; i++) {
                let obj = arr[i];
                delete obj.tempSortName;
                for (let prop in obj) {
                    if (Object.hasOwn(obj, prop)) {
                        var id = prop;
                    }
                }
                var item = obj[id];
                result.push(item);
            }
            return result;
        }
    },
    computed: {
        getFilters() {
            return this.filters;
        },
        getItemCount() {
            return Object.keys(this.selections).length;
        },
        getSelections() {
            return this.selections;
        },
        getTransition() {
            return this.transition;
        },
        getNewPlanDescription() {
            if (this.plan.description === "") {
                return null;
            } else {
                return this.plan.description;
            }
        },
        getCompletionMessage() {
            if (this.queryItems.isEdit) return "Updated";
            return "Created";
        },
        scrollHeight(){
            return window.innerHeight / 1.5; 
        }
    },
    watch: {
        // Watch for text in categories to change
        text(){
            this.fetchData();
        }
    }

}
</script>

<style scoped>
.bg-aqua-teal {
    background-color: #08b6aa;
    ;
}

/* Format Content Panel Height */
.content-panel-height {
    height: 50%;
}

/* Header Styling */
.header {
    background-color: #08b6aa;
    /* Teal background */
    padding: 1rem;
    color: white;
    margin-bottom: 1rem;
}

.review-button {
    /* margin-top: 2rem; */
    padding: 0.75rem 1.5rem;
    background-color: #ff6d44;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.review-button:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.review-button:hover:not(:disabled) {
    background-color: #e65a33;
}
</style>